import type { Tag } from "@engram/core";
import { parseTagIds } from "@engram/core/id";
import { Check, CopyPlus, Download, FolderInput, Merge, Minus, MoreHorizontal, Plus, RotateCcw, Search, Star, Tag as TagIcon, Trash2, X } from "lucide-react";
import { useState } from "react";
import { useLocation } from "wouter";
import {
  addShortcut, addTagToNotes, createTag, deleteNotesForever, duplicateNote, mergeNotes, moveNotes, removeTagFromNotes, restoreNotes, trashNotes,
} from "../db/actions";
import { useQuery } from "../db/client";
import { ExportDialog } from "./ExportLazy";
import { collator } from "./format";
import { Confirm, Menu, Popover, useAnchor, type Anchor } from "./Menu";
import { LocationDialog } from "./MoveDialog";

/* Actions on several notes at once: the panel beside the list (wide), the bar over the list (phone), and the note context menu. */

export type BulkOp = "move" | "copy" | "tags" | "export" | "delete" | "merge";

const plural = (n: number) => `${n} note${n === 1 ? "" : "s"}`;

/** Renders the picker or dialog for a bulk operation. */
export function BulkOpView({ op, anchor, ids, onClose, onDone }: { op: BulkOp; anchor: Anchor | null; ids: string[]; onClose: () => void; onDone: () => void }) {
  const one = useQuery<{ title: string; notebook_id: string }>(ids.length === 1 ? "SELECT title, notebook_id FROM notes WHERE id = ?" : null, [ids[0]], ["notes"])?.[0];
  const subject = ids.length === 1 ? (one?.title ?? "") : plural(ids.length);
  const first = useQuery<{ title: string }>(op === "merge" ? "SELECT title FROM notes WHERE id = ?" : null, [ids[0]], ["notes"])?.[0];
  const [, navigate] = useLocation();
  switch (op) {
    case "merge":
      return (
        <Confirm
          title={`Merge ${plural(ids.length)}?`}
          body={`They're combined into ${first ? `"${first.title || "Untitled"}"` : "the first note"} (the first one you selected), in the order you selected them. The others move to Trash.`}
          confirm="Merge"
          onConfirm={() =>
            void mergeNotes(ids).then((id) => {
              onDone();
              if (id) navigate(`/note/${id}`);
            })
          }
          onClose={onClose}
        />
      );
    case "move":
      return <LocationDialog title="Move" subject={subject} current={one?.notebook_id} onClose={onClose} onPick={(nb) => void moveNotes(ids, nb).then(onDone)} />;
    case "copy":
      return (
        <LocationDialog
          title="Copy to"
          subject={subject}
          onClose={onClose}
          onPick={async (nb) => {
            for (const id of ids) await duplicateNote(id, nb);
            onDone();
          }}
        />
      );
    case "tags":
      return anchor ? <TagPicker anchor={anchor} ids={ids} onClose={onClose} /> : null;
    case "export":
      return <ExportDialog scope={{ kind: "notes", ids, name: plural(ids.length) }} onClose={onClose} />;
    case "delete":
      return (
        <Confirm
          title="Delete permanently?"
          body={`${plural(ids.length)} will be deleted forever. This can't be undone.`}
          confirm="Delete"
          danger
          onConfirm={() => void deleteNotesForever(ids).then(onDone)}
          onClose={onClose}
        />
      );
  }
}

/** Tag several notes: ticked = every selected note has it; clicking toggles it on all of them. */
function TagPicker({ anchor, ids, onClose }: { anchor: Anchor; ids: string[]; onClose: () => void }) {
  const tags = useQuery<Tag>("SELECT * FROM tags WHERE deleted = 0", [], ["tags"]);
  const notes = useQuery<{ tag_ids: string }>(`SELECT tag_ids FROM notes WHERE id IN (${ids.map(() => "?").join(",")})`, ids, ["notes"]);
  const [q, setQ] = useState("");
  const count = new Map<string, number>();
  for (const n of notes ?? []) for (const t of parseTagIds(n.tag_ids)) count.set(t, (count.get(t) ?? 0) + 1);
  const list = (tags ?? []).filter((t) => t.name.toLowerCase().includes(q.trim().toLowerCase())).sort((a, b) => collator.compare(a.name, b.name));
  const exact = tags?.some((t) => t.name.toLowerCase() === q.trim().toLowerCase());
  const toggle = (t: string) => void (count.get(t) === ids.length ? removeTagFromNotes(ids, t) : addTagToNotes(ids, t));
  return (
    <Popover anchor={anchor} onClose={onClose} className="menu picker">
      <label className="find-input">
        <Search size={13} />
        <input
          autoFocus
          placeholder="Find or create a tag"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={async (e) => {
            if (e.key !== "Enter" || !q.trim()) return;
            const hit = list[0];
            if (hit && hit.name.toLowerCase() === q.trim().toLowerCase()) toggle(hit.id);
            else await addTagToNotes(ids, await createTag(q.trim()));
            setQ("");
          }}
        />
      </label>
      <div className="picker-list">
        {q.trim() && !exact && (
          <button
            className="menu-item"
            onClick={async () => {
              await addTagToNotes(ids, await createTag(q.trim()));
              setQ("");
            }}
          >
            <span className="menu-icon"><Plus size={14} /></span>
            <span className="menu-label">Create tag "{q.trim()}"</span>
          </button>
        )}
        {list.map((t) => {
          const n = count.get(t.id) ?? 0;
          return (
            <button key={t.id} className="menu-item" onClick={() => toggle(t.id)}>
              <span className="menu-icon">{n === ids.length ? <Check size={14} /> : n > 0 ? <Minus size={14} /> : <TagIcon size={13} />}</span>
              <span className="menu-label">{t.name}</span>
              {n > 0 && n < ids.length && <span className="menu-hint">{n} of {ids.length}</span>}
            </button>
          );
        })}
      </div>
    </Popover>
  );
}

/** Wide/compact layouts: shown in place of the editor while several notes are selected. */
export function MultiPanel({ ids, trash, onClear }: { ids: string[]; trash: boolean; onClear: () => void }) {
  const [op, setOp] = useState<{ op: BulkOp; anchor: HTMLElement | null } | null>(null);
  const start = (o: BulkOp) => (e: { currentTarget: HTMLElement }) => setOp({ op: o, anchor: e.currentTarget });
  return (
    <div className="multi-panel">
      <div className="multi-stack" aria-hidden>
        <span />
        <span />
        <span />
      </div>
      <h2>{plural(ids.length)} selected</h2>
      <div className="multi-actions">
        {trash ? (
          <>
            <button className="btn" onClick={() => void restoreNotes(ids).then(onClear)}><RotateCcw size={14} /> Restore</button>
            <button className="btn danger-outline" onClick={start("delete")}><Trash2 size={14} /> Delete permanently</button>
          </>
        ) : (
          <>
            <button className="btn" onClick={start("move")}><FolderInput size={14} /> Move</button>
            <button className="btn" onClick={start("copy")}><CopyPlus size={14} /> Copy to</button>
            <button className="btn" onClick={start("tags")}><TagIcon size={14} /> Tags</button>
            <button className="btn" onClick={async () => {
              // One at a time, so each gets the next position (in parallel they all read the same last one).
              for (const id of ids) await addShortcut("note", id);
            }}><Star size={14} /> Add to Shortcuts</button>
            <button className="btn" onClick={start("merge")}><Merge size={14} /> Merge</button>
            <button className="btn" onClick={start("export")}><Download size={14} /> Export</button>
            <button className="btn danger-outline" onClick={() => void trashNotes(ids).then(onClear)}><Trash2 size={14} /> Move to Trash</button>
          </>
        )}
      </div>
      <button className="link-btn" onClick={onClear}>Cancel</button>
      {op && <BulkOpView op={op.op} anchor={op.anchor} ids={ids} onClose={() => setOp(null)} onDone={onClear} />}
    </div>
  );
}

/** Phone: replaces the list header while selecting (long-press a note to start). */
export function SelectionBar({ ids, trash, onClear, onSelectAll }: { ids: string[]; trash: boolean; onClear: () => void; onSelectAll: () => void }) {
  const [op, setOp] = useState<{ op: BulkOp; anchor: HTMLElement | null } | null>(null);
  const more = useAnchor();
  const none = ids.length === 0;
  const start = (o: BulkOp) => (e: { currentTarget: HTMLElement }) => setOp({ op: o, anchor: e.currentTarget });
  return (
    <header className="list-header selection-bar">
      <button className="icon-btn" aria-label="Cancel selection" onClick={onClear}><X size={18} /></button>
      <span className="selection-count">{ids.length} selected</span>
      {trash ? (
        <>
          <button className="icon-btn" title="Restore" disabled={none} onClick={() => void restoreNotes(ids).then(onClear)}><RotateCcw size={17} /></button>
          <button className="icon-btn" title="Delete permanently" disabled={none} onClick={start("delete")}><Trash2 size={17} /></button>
        </>
      ) : (
        <>
          <button className="icon-btn" title="Move" disabled={none} onClick={start("move")}><FolderInput size={17} /></button>
          <button className="icon-btn" title="Tags" disabled={none} onClick={start("tags")}><TagIcon size={17} /></button>
          <button className="icon-btn" title="Move to Trash" disabled={none} onClick={() => void trashNotes(ids).then(onClear)}><Trash2 size={17} /></button>
        </>
      )}
      <button className="icon-btn" title="More" onClick={more.open}><MoreHorizontal size={17} /></button>
      {more.anchor && (
        <Menu
          anchor={more.anchor}
          align="end"
          onClose={more.close}
          entries={[
            { label: "Select all", onSelect: onSelectAll },
            ...(trash
              ? []
              : [
                  { label: "Copy to…", icon: <CopyPlus size={14} />, disabled: none, onSelect: () => setOp({ op: "copy", anchor: more.anchor }) },
                  { label: "Add to Shortcuts", icon: <Star size={14} />, disabled: none, onSelect: () => void Promise.all(ids.map((id) => addShortcut("note", id))).then(onClear) },
                  { label: "Merge…", icon: <Merge size={14} />, disabled: ids.length < 2, onSelect: () => setOp({ op: "merge", anchor: null }) },
                  { label: "Export…", icon: <Download size={14} />, disabled: none, onSelect: () => setOp({ op: "export", anchor: null }) },
                ]),
          ]}
        />
      )}
      {op && <BulkOpView op={op.op} anchor={op.anchor} ids={ids} onClose={() => setOp(null)} onDone={onClear} />}
    </header>
  );
}
